/**
 * FieldReviewChip — the per-field human-review indicator (item 4).
 *
 * One quiet indicator per critical field, not a badge farm (Carl's mantra).
 * States, all designed:
 *  - confirmed            → teal, calm check, who/when on hover title
 *  - needs_review (fresh)  → muted "Needs review"
 *  - changed_since_confirmed → coral "Changed — needs review" (was confirmed, value moved)
 *  - elevated staff also get a Confirm action (needs_review) or a subtle Reset (confirmed)
 *  - non-elevated users see the status only, no action (absence, not a teased disabled button)
 *  - busy → the action shows an in-flight label
 *
 * Confirmation is elevated-agency-only and value-aware: the parent passes the
 * exact value the reviewer is looking at, and the API rejects (409) if it moved.
 */
import { Check, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FieldReviewState } from '@/types/tenant';

interface FieldReviewChipProps {
  review?: FieldReviewState;
  elevated: boolean;
  busy?: boolean;
  onConfirm?: () => void;
  onReset?: () => void;
}

export function FieldReviewChip({
  review,
  elevated,
  busy = false,
  onConfirm,
  onReset,
}: FieldReviewChipProps) {
  const status = review?.status ?? 'needs_review';
  const changed = Boolean(review?.changed_since_confirmed);

  if (status === 'confirmed') {
    const who = review?.confirmed_by ? `Confirmed by ${review.confirmed_by}` : 'Confirmed';
    return (
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-flex items-center gap-1 text-[11px] font-medium text-teal-dark"
          title={who}
        >
          <Check className="size-3" />
          Confirmed
        </span>
        {elevated && (
          <button
            type="button"
            onClick={onReset}
            disabled={busy}
            className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50"
          >
            Reset
          </button>
        )}
      </span>
    );
  }

  // needs_review — distinguish "never confirmed" from "changed since confirmed"
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn(
          'inline-flex items-center gap-1 text-[11px] font-medium',
          changed ? 'text-coral-dark' : 'text-muted-foreground',
        )}
        title={changed ? 'The value changed since it was last confirmed' : 'Not yet reviewed by agency staff'}
      >
        {changed && <AlertCircle className="size-3" />}
        {changed ? 'Changed — needs review' : 'Needs review'}
      </span>
      {elevated && (
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-teal-dark underline underline-offset-2 hover:text-teal disabled:opacity-50"
        >
          {busy && <Loader2 className="size-3 animate-spin" />}
          Confirm
        </button>
      )}
    </span>
  );
}
